import {
  CORRECTION_RELEASE_MAX,
  CORRECTION_TEXT_MAX,
  canWriteCorrection,
  type TicketCorrection,
  type TicketDetail,
} from '@easyhelp/shared';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState, type FormEvent } from 'react';
import { useSetTicketCorrection } from '../../api/ticket-closure';
import { formatDateTime, personName } from '../../components/ticket';
import { DetailSection } from './DetailSection';
import { eventActor } from './event-actor';
import { ThreadText } from './ThreadText';
import { WithViaMcpAbbr } from './ViaMcp';

/**
 * Chi ha scritto la correzione e quando: «Luigi Verdi · 02/11/2026, 10:30», con «via MCP» accanto al
 * nome se l'ha registrata da Claude o Cursor (#173, DEC-015 decisione 6), in testo come nel thread.
 */
function correctionByline(correction: TicketCorrection): string {
  const author = eventActor({ actor: personName(correction.author), viaMcp: correction.viaMcp });
  return `${author} · ${formatDateTime(correction.at)}`;
}

/**
 * Blocco «Correzione dello sviluppo» (PRD-004 US-5, story #6): distinto dal thread, con autore,
 * momento, versione di rilascio e testo; resta nel registro dopo la chiusura. Compilarlo è
 * volontario (DEC-004): lo scrive chi ha lavorato il ticket, di solito lo sviluppo, prima che il
 * ticket sia chiuso con «Invia e chiudi», che lo cita nell'email a chi ha segnalato. Da `Risolto`
 * in poi è in sola lettura (per correggerlo si riapre).
 */
export function TicketCorrectionSection({
  ticket,
}: {
  ticket: Pick<TicketDetail, 'code' | 'state' | 'correction' | 'reporter'>;
}) {
  const { correction } = ticket;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [release, setRelease] = useState('');
  const save = useSetTicketCorrection(ticket.code);
  const editable = canWriteCorrection(ticket.state);

  function startEditing() {
    setText(correction?.text ?? '');
    setRelease(correction?.release ?? '');
    save.reset();
    setEditing(true);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (text.trim() === '' || save.isPending) return;
    save.mutate({ text, release }, { onSuccess: () => setEditing(false) });
  }

  return (
    <DetailSection title="Correzione dello sviluppo">
      {correction ? (
        <Stack spacing={2}>
          <Typography variant="caption" color="text.secondary">
            <WithViaMcpAbbr>{correctionByline(correction)}</WithViaMcpAbbr>
          </Typography>
          <ThreadText text={correction.text} />
          {correction.release && (
            <Stack direction="row">
              <Chip
                size="small"
                variant="outlined"
                icon={<Inventory2OutlinedIcon fontSize="small" />}
                label={`Rilascio ${correction.release}`}
              />
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary">
            Scritta da chi ha lavorato il ticket: resta nel registro dopo la chiusura.
          </Typography>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Nessuna correzione registrata. È facoltativa: se c&apos;è, l&apos;email di chiusura la riporta a chi ha
          segnalato.
        </Typography>
      )}

      {save.isSuccess && !editing && (
        <Alert severity="success" role="status">
          Correzione registrata.
        </Alert>
      )}

      {editable && !editing && (
        <Stack direction="row">
          <Button variant="outlined" size="small" onClick={startEditing}>
            {correction ? 'Aggiorna la correzione' : 'Scrivi la correzione'}
          </Button>
        </Stack>
      )}

      {editable && editing && (
        <Stack component="form" spacing={3} noValidate onSubmit={submit} aria-label="Correzione dello sviluppo">
          <TextField
            label="Cosa è stato corretto"
            multiline
            minRows={3}
            value={text}
            onChange={(event) => setText(event.target.value)}
            slotProps={{ htmlInput: { maxLength: CORRECTION_TEXT_MAX } }}
            helperText={`Alla chiusura questo testo parte come email a ${ticket.reporter.email}: scrivilo per chi ha segnalato, non per il team.`}
          />
          <TextField
            label="Versione di rilascio (facoltativa)"
            placeholder="es. 2.14.3 · in produzione dal 18/09"
            value={release}
            onChange={(event) => setRelease(event.target.value)}
            slotProps={{ htmlInput: { maxLength: CORRECTION_RELEASE_MAX } }}
          />
          {save.isError && <Alert severity="error">Correzione non salvata: {save.error.message}</Alert>}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => setEditing(false)} disabled={save.isPending}>
              Annulla
            </Button>
            <Button type="submit" variant="contained" disabled={text.trim() === '' || save.isPending}>
              {save.isPending ? 'Salvataggio…' : 'Salva la correzione'}
            </Button>
          </Stack>
        </Stack>
      )}
    </DetailSection>
  );
}
